import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'

import { URL_CONST } from 'utils/urlConsts'

import { selectIsBillingDisabled, selectNextResetLabel } from 'containers/Agents/creditSelectors'
import { dismissNotification } from 'containers/App/actions'
import { selectData } from 'containers/App/selectors'
import SystemNotifications from 'containers/Navigation/SystemNotifications'

import { CreditsAlt } from 'components/lib/DesignSystem/Icons'
import IconButton from 'components/lib/DesignSystem/V3/IconButton'
import { resolveCreditNotification } from 'components/pages/navigation/SystemNotifications'

import {
  formatPsimaGeneratedCards,
  formatPsimaLaunchDelivery,
  hasPasswordExpiryWarning,
  NOTIFICATION_SPECS,
  resolveNotificationTitle,
} from './notificationSpecs'

// vuesax/outline/notification, 24×24, three filled paths (body, top knob,
// clapper). Root must not be fill="none": IconButton restrokes those svgs
// and the knob and clapper disappear. currentColor follows the nav text.
const BellIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      d="M12.02 20.5299C9.68999 20.5299 7.35999 20.1599 5.14999 19.4199C4.30999 19.1299 3.66999 18.5399 3.38999 17.7699C3.09999 16.9999 3.19999 16.1499 3.65999 15.3899L4.80999 13.4799C5.04999 13.0799 5.26999 12.2799 5.26999 11.8099V8.91992C5.26999 5.19992 8.29999 2.16992 12.02 2.16992C15.74 2.16992 18.77 5.19992 18.77 8.91992V11.8099C18.77 12.2699 18.99 13.0799 19.23 13.4899L20.37 15.3899C20.8 16.1099 20.88 16.9799 20.59 17.7699C20.3 18.5599 19.67 19.1599 18.88 19.4199C16.68 20.1599 14.35 20.5299 12.02 20.5299ZM12.02 3.66992C9.12999 3.66992 6.76999 6.01992 6.76999 8.91992V11.8099C6.76999 12.5399 6.46999 13.6199 6.09999 14.2499L4.94999 16.1599C4.72999 16.5299 4.66999 16.9199 4.79999 17.2499C4.91999 17.5899 5.21999 17.8499 5.62999 17.9899C9.80999 19.3899 14.24 19.3899 18.42 17.9899C18.78 17.8699 19.06 17.5999 19.19 17.2399C19.32 16.8799 19.29 16.4899 19.09 16.1599L17.94 14.2499C17.56 13.5999 17.27 12.5299 17.27 11.7999V8.91992C17.27 6.01992 14.92 3.66992 12.02 3.66992Z"
      fill="currentColor"
    />
    <path
      d="M13.88 3.94018C13.81 3.94018 13.74 3.93018 13.67 3.91018C13.38 3.83018 13.1 3.77018 12.83 3.73018C11.98 3.62018 11.16 3.68018 10.39 3.91018C10.11 4.00018 9.80999 3.91018 9.61999 3.70018C9.42999 3.49018 9.36999 3.19018 9.47999 2.92018C9.88999 1.87018 10.89 1.18018 12.03 1.18018C13.17 1.18018 14.17 1.86018 14.58 2.92018C14.68 3.19018 14.63 3.49018 14.44 3.70018C14.29 3.86018 14.08 3.94018 13.88 3.94018Z"
      fill="currentColor"
    />
    <path
      d="M12.02 22.8101C11.03 22.8101 10.07 22.4101 9.37002 21.7101C8.67002 21.0101 8.27002 20.0501 8.27002 19.0601H9.77002C9.77002 19.6501 10.01 20.2301 10.43 20.6501C10.85 21.0701 11.43 21.3101 12.02 21.3101C13.26 21.3101 14.27 20.3001 14.27 19.0601H15.77C15.77 21.1301 14.09 22.8101 12.02 22.8101Z"
      fill="currentColor"
    />
  </svg>
)

const BadgeDot = styled.span`
  position: absolute;
  top: 2px;
  right: 3px;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  background: var(--negative-color);
  color: var(--top-navigation-toggle-text);
  font-size: 10px;
  font-weight: 500;
  font-family: Poppins, sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  box-sizing: border-box;
  pointer-events: none;
  line-height: 1;
`

// Same hover as a collapsed rail icon: pale rounded square, glyph color
// unchanged. A circle that paints the bell white reads heavier than the rail.
const TriggerWrapper = styled.div`
  position: relative;
  display: inline-flex;

  > button:not(:disabled) {
    color: var(--top-navigation-text);
    width: 40px;
    height: 40px;
    border-radius: 12px;
    box-sizing: border-box;
  }

  > button svg {
    width: 24px;
    height: 24px;
  }

  > button:not(:disabled):hover,
  > button:not(:disabled):active {
    background-color: var(--sidebar-item-hover-background);
    color: var(--top-navigation-text);
  }
`

const DropdownPanel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: var(--z-index-navigation, 1000);
  background: var(--color-elevated-solid);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  box-shadow:
    0px 4px 6px -1px rgba(0, 0, 0, 0.1),
    0px 2px 4px -2px rgba(0, 0, 0, 0.1);
  min-width: 305px;
  max-height: 480px;
  overflow-y: auto;
`

const PanelTitle = styled.div`
  color: var(--primary-color);
  font-weight: 600;
  font-size: 12px;
  padding: 15px 20px;
  border-bottom: 1px solid var(--color-border);
  word-break: break-word;
`

const NotifRow = styled.div`
  display: flex;
  flex-direction: ${({ $stackCta }) => ($stackCta ? 'column' : 'row')};
  align-items: flex-start;
  gap: ${({ $stackCta }) => ($stackCta ? '12px' : '10px')};
  padding: ${({ $stackCta }) => ($stackCta ? '16px' : '12px 20px')};
  border-bottom: 1px solid var(--color-border);
  transition: background-color 0.12s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--top-navigation-dropdown-item-hover-bg);
  }
`

const NotifMain = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`

const NotifIcon = styled.span`
  flex-shrink: 0;
  display: inline-flex;
`

const NotifBody = styled.div`
  flex: 1;
  min-width: 0;
`

const NotifTitle = styled.div`
  color: ${({ $muted }) =>
    $muted ? 'var(--notification-credit-title-color)' : 'var(--primary-color)'};
  font-weight: 600;
  font-size: 12px;
  line-height: ${({ $muted }) => ($muted ? '16px' : '1.4')};
`

const NotifText = styled.div`
  color: ${({ $muted }) => ($muted ? 'var(--sub-text-color)' : 'var(--primary-color)')};
  font-size: ${({ $muted }) => ($muted ? '12px' : '11px')};
  line-height: ${({ $muted }) => ($muted ? '16px' : '1.5')};
  margin-top: 4px;
`

const NotifReset = styled.div`
  color: var(--notification-credit-reset-color);
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
`

const NotifCta = styled.a`
  display: inline-block;
  margin-top: ${({ $flush }) => ($flush ? '0' : '8px')};
  color: ${({ $flush }) => ($flush ? 'var(--ds-blue-2)' : 'var(--secondary-color)')};
  font-size: ${({ $flush }) => ($flush ? '12px' : '11px')};
  font-weight: ${({ $flush }) => ($flush ? '400' : '600')};
  line-height: ${({ $flush }) => ($flush ? '16px' : 'normal')};
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`

const EmptyRow = styled.div`
  color: var(--primary-color);
  font-size: 11px;
  padding: 12px 20px;
`

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`

// Stable identity: the legacy SystemNotifications/NavigationItem list these in
// effect dependency arrays, so a fresh arrow per render re-runs those effects.
const NOOP = () => {}

/* eslint-disable camelcase */

/**
 * Map live `state.app.data.notifications` to an ordered list of spec entries.
 *
 * Returns: [{ key, spec, to }] where `to` is the resolved route for route CTAs
 * (deepfake gets the timestamp query param). Each entry's `key` is unique so it
 * can be used as a React key (re_integration messages are suffixed by index).
 *
 * `handledKeys` collects every raw notification data key this mapping consumed,
 * so the caller can detect any *unmapped* truthy keys and fall back to the
 * legacy SystemNotifications rendering for them (never drop silently).
 */
function buildRenderedNotifications(notifications, { hideCredits = false, resetLabel } = {}) {
  const data = notifications || {}
  const items = []
  const handledKeys = new Set([
    // gating-only keys that never render on their own
    'license_poc',
  ])

  const push = (specKey, { key, to } = {}) => {
    const spec = NOTIFICATION_SPECS[specKey]
    if (!spec) return
    items.push({ key: key || specKey, spec, to: to ?? spec.cta?.action?.to })
  }

  const pushFormatted = (key, formatted) => {
    if (!formatted) return
    items.push({
      key,
      to: formatted.to,
      dismiss: formatted.dismiss,
      spec: {
        title: formatted.title,
        text: () => formatted.text,
        cta: { label: formatted.ctaLabel, action: { type: 'route', to: formatted.to } },
      },
    })
  }

  // License (mutually exclusive, suppressed for POC accounts)
  handledKeys.add('license_expired')
  handledKeys.add('license_warn')
  if (!data.license_poc) {
    if (data.license_expired) push('license_expired')
    else if (data.license_warn) push('license_warn')
  }

  handledKeys.add('license_limit_reached')
  if (data.license_limit_reached) push('license_limit_reached')

  handledKeys.add('mailbox_911_disable_reason')
  if (data.mailbox_911_disable_reason) push('mailbox_911_disable_reason')

  handledKeys.add('warn_days_to_password_expire')
  if (hasPasswordExpiryWarning(data.warn_days_to_password_expire)) {
    push('warn_days_to_password_expire')
  }

  handledKeys.add('is_office365_old_oauth_scope')
  if (data.is_office365_old_oauth_scope) push('is_office365_old_oauth_scope')

  handledKeys.add('re_integration_messages')
  if (Array.isArray(data.re_integration_messages)) {
    data.re_integration_messages.forEach((messageId, idx) => {
      const specKey = `re_integration_message_${messageId}`
      if (NOTIFICATION_SPECS[specKey]) {
        push(specKey, { key: `${specKey}_${idx}` })
      }
    })
  }

  handledKeys.add('deepfake')
  const deepfakeTs = data.deepfake?.suspicious_meetings_since_last_visit_timestamp
  if (deepfakeTs) {
    push('deepfake', {
      to: `${NOTIFICATION_SPECS.deepfake.cta.action.to}?notification=suspicious&timestamp=${deepfakeTs}`,
    })
  }

  handledKeys.add('psima_latest_generated_cards')
  pushFormatted(
    'psima_latest_generated_cards',
    formatPsimaGeneratedCards(data.psima_latest_generated_cards)
  )

  handledKeys.add('psima_simulations_launch_and_delivery')
  if (Array.isArray(data.psima_simulations_launch_and_delivery)) {
    data.psima_simulations_launch_and_delivery.forEach((entry) => {
      pushFormatted(
        `psima_simulations_launch_and_delivery_${entry.id}`,
        formatPsimaLaunchDelivery(entry)
      )
    })
  }

  handledKeys.add('ai_credits')
  const creditNotif = hideCredits ? null : resolveCreditNotification(data.ai_credits, resetLabel)
  if (creditNotif) {
    items.push({
      key: 'ai_credits',
      to: URL_CONST.settings.creditsBilling,
      spec: {
        title: creditNotif.title,
        text: () => creditNotif.subtext,
        resetLine: creditNotif.resetLine,
        // Light mode is blue-950 via --ds-v3-icon-button-color. CreditsAlt paints
        // both circles with that one fill.
        icon: <CreditsAlt width="20px" height="20px" fill="var(--ds-v3-icon-button-color)" />,
        iconLabel: 'AI credits',
        ctaOutside: true,
        cta: {
          label: creditNotif.cta,
          action: { type: 'route', to: URL_CONST.settings.creditsBilling },
        },
      },
    })
  }

  return { items, handledKeys }
}

/**
 * `state.app.data.notifications` is NOT a list of notifications — it is a mixed
 * bag of account config that happens to also carry the notification triggers.
 * These keys are configuration or inputs to the specs above (`license_expiery`
 * and `login_expiry` are the dates the license notifications interpolate), so a
 * truthy value here means nothing is un-rendered. Without this list every tenant
 * has truthy config and the legacy fallback below mounts unconditionally.
 */
const NON_NOTIFICATION_KEYS = new Set([
  'allow_bablic',
  'allow_external_campaigns',
  'exchange_integration_status',
  'hide_personal_data',
  'in_test_mode',
  'isIronTrapsLicensed',
  'license_expiery', // input to license_warn's copy
  'license_federation',
  'login_expiry', // input to license_expired's copy
  'mixpanel_token',
  'show_threat_assessment',
])

/**
 * Detect notification data keys that carry content but are NOT consumed by the
 * spec mapping — those fall back to the legacy SystemNotifications rendering so
 * a newly-introduced backend notification is never dropped silently.
 *
 * An empty object is not content: the payload-style keys (`deepfake`, `psima`)
 * are always present and arrive as `{}` when there is nothing to report, so
 * `Boolean({})` alone would report every tenant as having unmapped data.
 */
function hasUnmappedNotifications(notifications, handledKeys) {
  const data = notifications || {}
  return Object.keys(data).some((rawKey) => {
    if (handledKeys.has(rawKey) || NON_NOTIFICATION_KEYS.has(rawKey)) return false
    const value = data[rawKey]
    if (Array.isArray(value)) return value.length > 0
    if (value && typeof value === 'object') return Object.keys(value).length > 0
    return Boolean(value)
  })
}

/* eslint-enable camelcase */

/**
 * NotificationsMenu — bell button with numeric count badge.
 *
 * Renders the notification panel from the declarative NOTIFICATION_SPECS map
 * (notificationSpecs.js) against `state.app.data.notifications`:
 *   - bold title, body text, single CTA (mailto via <a>, routes via history)
 *   - badge count = number of rendered notifications
 *   - any unmapped/unknown notification key falls back to the legacy
 *     SystemNotifications rendering so nothing is dropped silently.
 *
 * @param {object}   props
 * @param {string}  [props.className]
 */
const NotificationsMenu = ({ className }) => {
  const [open, setOpen] = useState(false)
  // deepfake CTA is "click to dismiss" in the legacy UI; preserve that.
  const [hasClickedDeepfake, setHasClickedDeepfake] = useState(false)
  const [dismissedKeys, setDismissedKeys] = useState([])
  const wrapperRef = useRef(null)
  const history = useHistory()
  const dispatch = useDispatch()
  const appData = useSelector(selectData)
  const isBillingDisabled = useSelector(selectIsBillingDisabled)
  const creditResetLabel = useSelector(selectNextResetLabel)

  const { items, hasUnmapped } = useMemo(() => {
    const notifications = appData?.notifications
    const { items: built, handledKeys } = buildRenderedNotifications(notifications, {
      hideCredits: isBillingDisabled,
      resetLabel: creditResetLabel,
    })
    const filtered = built.filter(
      (item) =>
        !(item.key === 'deepfake' && hasClickedDeepfake) && !dismissedKeys.includes(item.key)
    )
    return {
      items: filtered,
      hasUnmapped: hasUnmappedNotifications(notifications, handledKeys),
    }
  }, [appData, dismissedKeys, hasClickedDeepfake, isBillingDisabled, creditResetLabel])

  const count = items.length

  const close = useCallback(() => setOpen(false), [])

  const handleCtaClick = useCallback(
    (item) => (e) => {
      const { action } = item.spec.cta
      if (action.type === 'route') {
        e.preventDefault()
        if (item.key === 'deepfake') setHasClickedDeepfake(true)
        if (item.dismiss) {
          dispatch(dismissNotification(item.dismiss))
          setDismissedKeys((keys) => (keys.includes(item.key) ? keys : [...keys, item.key]))
        }
        close()
        history.push(item.to)
      }
      // mailto: let the browser handle the <a href> default
    },
    [close, dispatch, history]
  )

  // Outside-click handler
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        close()
      }
    }
    if (open) {
      document.addEventListener('mousedown', handler)
    }
    return () => document.removeEventListener('mousedown', handler)
  }, [open, close])

  return (
    <Wrapper className={className} ref={wrapperRef}>
      <TriggerWrapper>
        <IconButton
          id="top-bar-notifications-btn"
          dataPendoFeature="nav-notifications-open"
          ariaLabel="System Notifications"
          onClick={() => setOpen((v) => !v)}
          size="small"
        >
          <BellIcon />
        </IconButton>
        {count > 0 && (
          <BadgeDot id="top-bar-notifications-badge" aria-label={`${count} notifications`}>
            {count > 99 ? '99+' : count}
          </BadgeDot>
        )}
      </TriggerWrapper>

      {open && (
        <DropdownPanel id="top-bar-notifications-panel" role="dialog" aria-label="Notifications">
          <PanelTitle>System Notifications</PanelTitle>

          {items.map((item) => {
            const { spec } = item
            const { action } = spec.cta
            const cta = (
              <NotifCta
                id={`top-bar-notifications-cta-${item.key}`}
                href={action.type === 'mailto' ? action.href : item.to}
                onClick={handleCtaClick(item)}
                $flush={spec.ctaOutside}
              >
                {spec.cta.label}
              </NotifCta>
            )
            const icon = spec.icon && (
              <NotifIcon role="img" aria-label={spec.iconLabel}>
                {spec.icon}
              </NotifIcon>
            )
            const body = (
              <NotifBody>
                <NotifTitle $muted={spec.ctaOutside}>
                  {resolveNotificationTitle(spec, appData?.notifications || {}, appData)}
                </NotifTitle>
                <NotifText $muted={spec.ctaOutside}>
                  {spec.text(appData?.notifications || {}, appData)}
                </NotifText>
                {spec.resetLine && <NotifReset>{spec.resetLine}</NotifReset>}
                {!spec.ctaOutside && cta}
              </NotifBody>
            )
            const content = (
              <>
                {icon}
                {body}
              </>
            )
            return (
              <NotifRow key={item.key} className="notif-container" $stackCta={spec.ctaOutside}>
                {spec.ctaOutside ? <NotifMain>{content}</NotifMain> : content}
                {spec.ctaOutside && cta}
              </NotifRow>
            )
          })}

          {/*
           * Fallback: if app state carries notification keys the spec map does
           * not recognize, render the legacy connected SystemNotifications so
           * those notifications are never dropped silently. (Today the spec map
           * covers every key the legacy component renders, so this stays hidden
           * unless the backend introduces a new, unmapped key.)
           */}
          {hasUnmapped && (
            <SystemNotifications
              title="Other Notifications"
              setBadgeCount={NOOP}
              setOnClickCallback={NOOP}
              setDropdownShown={NOOP}
              /*
               * NOT `close`. In the legacy nav this reported "the popup you own
               * closed", and NavigationItem calls it from a mount effect while
               * its own `showModal` is still false — passing our `close` here
               * shut this panel ~14ms after it opened, so the bell looked dead.
               * Embedded inline, that legacy popup state is not ours to track.
               */
              onCloseCallback={NOOP}
            />
          )}

          {count === 0 && !hasUnmapped && <EmptyRow>No Notifications</EmptyRow>}
        </DropdownPanel>
      )}
    </Wrapper>
  )
}

export default NotificationsMenu
